import {
  Audit,
  ASN,
  Case,
  Container,
  Damaged,
  FreightShipment,
  Generic,
  Pallet,
  ParcelShipment,
  Place,
  Product,
  Transload,
  Truckload,
} from 'src/api/models'
import { Action, ApplyScan } from './interface'
import { Handler } from './base'
import { AssignmentHandler } from './AssignmentHandler'
import { AuditHandler } from './AuditHandler'
import { FoundHandler } from './FoundHandler'
import { MoveContainerHandler } from './MoveContainerHandler'
import { MoveHandler } from './MoveHandler'
import { ReceivingHandler } from './ReceivingHandler'
import { LabelPrintingHandler } from './LabelPrintingHandler'
import { CaseLabelPrintingHandler } from './CaseLabelPrintingHandler'
import { SetSSCCHandler } from './SetSSCCHandler'

export class PreviewHandler extends Handler {
  obj?: Generic

  applyFound = () => {
    const handler = new FoundHandler({ cache: this.cache, source: this.obj as Generic })
    return handler
  }

  applyAudit = () => {
    const handler = new AuditHandler({ cache: this.cache, audit: this.obj as Generic })
    return handler
  }

  applyPrint = () => {
    if ( this.obj?.object.order?.retailer.labelRequirement === "ca"
      || (
        this.obj?.object.order?.retailer.labelRequirement === "pc" && this.obj?.object.carrier.carrierType === "parcel"
      )
    ){
      return new CaseLabelPrintingHandler({ cache: this.cache, obj: this.obj as Generic })
    } else {
      return new LabelPrintingHandler({ cache: this.cache, obj: this.obj as Generic })
    }
  }

  applySetSSCC = () => {
    const handler = new SetSSCCHandler({ cache: this.cache, obj: this.obj as Generic })
    return handler
  }

  apply = ({ scanned, context, local }: ApplyScan): [Action, any | undefined] => {
    if (!this.obj) {
      // First scan. Simply track what was scanned.
      this.obj = scanned
      return [Action.None, undefined]
    } else if (this.obj.object.guid === scanned.object.guid) {
      // Double scan. Navigate to that page.
      return [Action.Navigate, this.obj]
    }
    let action: Action = Action.None
    let data: any
    // Handle scan sequence logic
    switch (this.obj?.object?.type) {
      case Place.type: {
        [action, data] = this.applyPlaceSequence({ scanned, context, local })
        break
      }
      case Container.type:
      case Pallet.type: {
        [action, data] = this.applyContainerSequence({ scanned, context, local })
        break
      }
      case FreightShipment.type:
      case ParcelShipment.type: {
        [action, data] = this.applyShipmentSequence({ scanned, context, local })
        break
      }
      case ASN.type: {
        [action, data] = this.applyASNSequence({ scanned, context, local })
        break
      }
      case Audit.type: {
        [action, data] = this.applyAuditSequence({ scanned, context, local })
        break
      }
      case Truckload.type: {
        [action, data] = this.applyTruckloadSequence({ scanned, context, local })
        break
      }
      case Transload.type: {
        [action, data] = this.applyTransloadSequence({ scanned, context, local })
        break
      }
    }
    if (action === Action.None) {
      this.obj = scanned
    }
    return [action, data]
  }

  applyPlaceSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Case.type:
      case Product.type: {
        const handler = new MoveHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Transload.type:
      case Container.type:
      case Pallet.type: {
        const handler = new MoveContainerHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Damaged.type: {
        const handler = new MoveHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyTransloadSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Place.type: {
        const handler = new AssignmentHandler({ cache: this.cache, obj: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyContainerSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Case.type:
      case Product.type: {
        const handler = new MoveHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Place.type: {
        const handler = new AssignmentHandler({ cache: this.cache, obj: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Damaged.type: {
        const handler = new MoveHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyShipmentSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Case.type:
      case Product.type: {
        const handler = new MoveHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Transload.type:
      case Container.type:
      case Pallet.type: {
        const handler = new MoveContainerHandler({ cache: this.cache, source: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
      case Place.type: {
        const handler = new AssignmentHandler({ cache: this.cache, obj: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyTruckloadSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Place.type: {
        const handler = new AssignmentHandler({ cache: this.cache, obj: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyASNSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Container.type:
      case Pallet.type:
      case Place.type: {
        const handler = new ReceivingHandler({ cache: this.cache, asn: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  applyAuditSequence({ scanned, context, local }: ApplyScan): [Action, any | undefined] {
    switch (scanned.object.type) {
      case Place.type: {
        const handler = new AuditHandler({ cache: this.cache, audit: this.obj as Generic })
        handler.apply({ scanned, context, local })
        return [Action.ChangeHandler, handler]
      }
    }
    return [Action.None, undefined]
  }

  getContext = () => {
    return {
      activity: 'previewing',
      preview: this.obj?.object?.guid,
    }
  }
}
